import { useEffect, useSyncExternalStore } from 'react';
import { fetchCities } from '../services/neighborhoodMapService';
import {
  getFeaturedCities,
  matchSaudiCity,
  resolveCityRows,
  searchSaudiCities,
  type ResolvedSaudiCity,
} from '../config/saudiCities';

export type { ResolvedSaudiCity } from '../config/saudiCities';

/**
 * The canonical Saudi city list (config/saudiCities) with each city's database
 * id attached, for every city picker.
 *
 * - Names are available immediately: `cities` is never empty, even before the
 *   fetch finishes or when it fails — a city without a `cityId` still searches by
 *   name (the `city` param), so the picker never blocks on the network.
 * - Ids come from the existing `GET /locations/cities` (cached 5 minutes server
 *   side), fetched ONCE per page load and shared by every component through a
 *   module-level store — no provider needed.
 * - Read-only: placeholder / foreign / unmatched rows are skipped, never changed.
 */
export interface UseSaudiCitiesResult {
  /** Featured cities first (owner's order), then the rest by Arabic name. */
  cities: ResolvedSaudiCity[];
  /** مكة المكرمة، المدينة المنورة، الرياض، جدة، الدمام. */
  featured: ResolvedSaudiCity[];
  /** True until the database ids have loaded (names are usable meanwhile). */
  loading: boolean;
  /** The ids could not be loaded; cities still work by name. */
  error: Error | null;
  findBySlug: (slug: string | null | undefined) => ResolvedSaudiCity | undefined;
  /** Any spelling a person or a database row uses («جده», "Mecca", «الإحساء»…). */
  findByName: (name: string | null | undefined) => ResolvedSaudiCity | undefined;
  /** The canonical city a database City.id (e.g. a `?cityId=` URL param) belongs to. */
  findByCityId: (cityId: string | null | undefined) => ResolvedSaudiCity | undefined;
  /** Type-ahead over Arabic/English names and aliases (see searchSaudiCities); '' → all, in `cities` order. */
  search: (query: string) => ResolvedSaudiCity[];
}

interface CitiesState {
  cities: ResolvedSaudiCity[];
  loading: boolean;
  error: Error | null;
}

// A failed fetch is retried by the next component that mounts, at most this often.
const RETRY_AFTER_MS = 30_000;

let state: CitiesState = { cities: resolveCityRows([]), loading: true, error: null };
let status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
let failedAt = 0;
const listeners = new Set<() => void>();

function setState(next: CitiesState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): CitiesState {
  return state;
}

function ensureLoaded(): void {
  if (status === 'loading' || status === 'ready') return;
  if (status === 'error' && Date.now() - failedAt < RETRY_AFTER_MS) return;
  status = 'loading';
  if (!state.loading || state.error) setState({ ...state, loading: true, error: null });
  fetchCities()
    .then((rows) => {
      status = 'ready';
      setState({ cities: resolveCityRows(Array.isArray(rows) ? rows : []), loading: false, error: null });
    })
    .catch((err: unknown) => {
      status = 'error';
      failedAt = Date.now();
      setState({ ...state, loading: false, error: err instanceof Error ? err : new Error(String(err)) });
    });
}

// One result object per state, shared by every consumer, so the finder
// functions keep a stable identity until the data actually changes.
let cachedFor: CitiesState | null = null;
let cachedResult: UseSaudiCitiesResult | null = null;

function toResult(current: CitiesState): UseSaudiCitiesResult {
  if (cachedFor === current && cachedResult) return cachedResult;
  const bySlug = new Map(current.cities.map((city) => [city.slug, city]));
  const byCityId = new Map<string, ResolvedSaudiCity>();
  for (const city of current.cities) {
    if (city.cityId) byCityId.set(city.cityId, city);
  }
  cachedResult = {
    cities: current.cities,
    featured: getFeaturedCities(current.cities),
    loading: current.loading,
    error: current.error,
    findBySlug: (slug) => (slug ? bySlug.get(slug) : undefined),
    findByName: (name) => {
      const match = matchSaudiCity(name);
      return match ? bySlug.get(match.slug) : undefined;
    },
    findByCityId: (cityId) => (cityId ? byCityId.get(cityId) : undefined),
    search: (query) => searchSaudiCities(query, current.cities),
  };
  cachedFor = current;
  return cachedResult;
}

export function useSaudiCities(): UseSaudiCitiesResult {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    ensureLoaded();
  }, []);
  return toResult(current);
}
